import { CustomCalendarType } from "@ac-expo/api-types";
import { getFrenchMonthName } from "@ac-expo/utils";
import dayjs from "dayjs";
import { useMemo } from "react";
import { cn } from "../../../utils";
import { CaretLeftIcon, CaretRightIcon } from "../../icons";
import { FiltersBars } from "./filter-bars";
import {
  DAY_HEADERS_SHORT,
  getActivityBackgroundColor,
  getActivitySpansForWeek,
  getBackgroundContainerStyle,
  getBackgroundOverlayStyle,
  getDaysOfWeek,
  GRID_COLUMN_DIVIDERS,
  PlanningEvent,
  WeekGroup,
} from "./utils";

interface VerticalWeeklyCalendarProps {
  groupedByWeek: WeekGroup[];
  filteredSlots: PlanningEvent[];
  groupedPlaces: Record<string, PlanningEvent[]>;
  currentDay: dayjs.Dayjs;
  isFirstMonth: boolean;
  isLastMonth: boolean;
  handlePrev: () => void;
  handleNext: () => void;
  setPlaceFilter: (place: string | undefined) => void;
  onOpenActivitiesDetails?: (events: PlanningEvent[]) => void;
  isRegisterEnabled?: boolean;
  customCalendar?: CustomCalendarType;
  backgroundStyleOptions: {
    backgroundPictureUrl?: string;
    backgroundColor?: string;
    opacity?: number;
  };
}

export const VerticalWeeklyCalendar = ({
  groupedByWeek,
  filteredSlots,
  groupedPlaces,
  currentDay,
  isFirstMonth,
  isLastMonth,
  handlePrev,
  handleNext,
  setPlaceFilter,
  onOpenActivitiesDetails,
  isRegisterEnabled,
  customCalendar,
  backgroundStyleOptions,
}: VerticalWeeklyCalendarProps) => {
  const today = dayjs();
  const weeklyMonthYear = `${getFrenchMonthName(currentDay.month()).toUpperCase()} ${currentDay.year()}`;

  const allWeeksOfMonth = useMemo(() => {
    const monthStart = currentDay.startOf("month");
    const monthEnd = currentDay.endOf("month");
    const weeks: WeekGroup[] = [];

    let weekStart = monthStart.startOf("week");
    while (weekStart.isSameOrBefore(monthEnd, "day")) {
      const weekEnd = weekStart.endOf("week");
      const existing = groupedByWeek.find((w) =>
        dayjs(w.weekStart).isSame(weekStart, "day")
      );
      weeks.push({
        weekStart: weekStart.toDate(),
        weekEnd: weekEnd.toDate(),
        activities: existing?.activities ?? [],
      });
      weekStart = weekStart.add(1, "week");
    }
    return weeks;
  }, [currentDay, groupedByWeek]);

  return (
    <div>
      <div className="gap-6 rounded-t-xl px-6 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <p className="font-extrabold text-black">{weeklyMonthYear}</p>
            <button type="button" onClick={handlePrev} disabled={isFirstMonth}>
              <CaretLeftIcon
                className={cn("cursor-pointer text-xl", {
                  "cursor-not-allowed": isFirstMonth,
                })}
                width={32}
                height={32}
                fill={isFirstMonth ? "#e4e4e4" : "#287BB9"}
              />
            </button>
            <button type="button" onClick={handleNext} disabled={isLastMonth}>
              <CaretRightIcon
                className={cn("cursor-pointer text-xl", {
                  "cursor-not-allowed": isLastMonth,
                })}
                width={32}
                height={32}
                fill={isLastMonth ? "#e4e4e4" : "#287BB9"}
              />
            </button>
          </div>
          <FiltersBars
            slots={filteredSlots}
            onOpenActivitiesDetails={onOpenActivitiesDetails}
            placeNames={Object.keys(groupedPlaces).filter(
              (place) => place.length > 0
            )}
            setPlaceFilter={setPlaceFilter}
          />
        </div>
      </div>
      <div
        className="relative mt-4 size-full"
        style={getBackgroundContainerStyle(backgroundStyleOptions)}
      >
        <div
          className="size-full overflow-hidden object-fill"
          style={getBackgroundOverlayStyle(backgroundStyleOptions)}
        />
        <div className="relative z-10">
          {allWeeksOfMonth.map((week, weekIndex) => {
            const weekDays = getDaysOfWeek(week.weekStart);
            const activitySpans = getActivitySpansForWeek(
              week.activities,
              weekDays
            );

            return (
              <div key={weekIndex} className="border border-gray-200">
                <div className="grid grid-cols-7">
                  {weekDays.map((day, dayIndex) => {
                    const dayObj = dayjs(day);
                    const isToday = dayObj.isSame(today, "day");
                    return (
                      <div
                        key={dayIndex}
                        className={cn(
                          "flex items-center justify-center border-b bg-white border-gray-200 px-2 py-1",
                          dayIndex > 0 && "border-l"
                        )}
                      >
                        <span
                          className={cn(
                            "rounded-lg px-3 py-1 text-sm font-semibold",
                            isToday
                              ? "bg-orange-500 text-white"
                              : "text-gray-600"
                          )}
                        >
                          {DAY_HEADERS_SHORT[dayIndex]} {dayObj.format("DD/MM")}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="relative grid min-h-28 grid-cols-7">
                  {GRID_COLUMN_DIVIDERS.map((i) => (
                    <div
                      key={i}
                      className="absolute top-0 h-full border-l border-gray-200"
                      style={{ left: `${(i / 7) * 100}%` }}
                    />
                  ))}

                  <div className="col-span-7 flex flex-col gap-1 p-1">
                    {activitySpans.map((span, spanIndex) => {
                      const leftPercent = (span.startColIndex / 7) * 100;
                      const widthPercent = (span.span / 7) * 100;

                      return (
                        <button
                          type="button"
                          key={spanIndex}
                          className="relative cursor-pointer rounded px-1 py-0.5 text-left text-xs text-white"
                          style={{
                            backgroundColor: getActivityBackgroundColor(
                              span.activity.allDay,
                              customCalendar?.fullDayColor,
                              span.activity.blockColor
                            ),
                            marginLeft: `${leftPercent}%`,
                            width: `${widthPercent}%`,
                          }}
                          onClick={() =>
                            onOpenActivitiesDetails?.([span.activity])
                          }
                        >
                          {span.activity.allDay ? (
                            <div className="text-xs font-semibold">
                              Jour entier
                            </div>
                          ) : (
                            <div className="text-xs font-semibold">
                              {dayjs(span.activity.start).format("HH:mm")} -{" "}
                              {dayjs(span.activity.end).format("HH:mm")}
                            </div>
                          )}
                          <div className="text-sm truncate font-bold">
                            {span.activity.title}
                          </div>
                          {isRegisterEnabled &&
                            span.activity.isRegistrationRequired &&
                            span.activity.hasAvailableTicketTypes !== false && (
                              <div className="mt-0.5 text-xs underline">
                                S&apos;inscrire
                              </div>
                            )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
          {allWeeksOfMonth.every((w) => w.activities.length === 0) && (
            <div className="py-8 text-center text-gray-500">
              Aucune animation pour ce mois
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
